/**
 * Utility functions for handling API errors consistently across the frontend
 */

/**
 * Converts API error responses to user-friendly string messages
 * Handles various error formats:
 * - String errors
 * - Pydantic validation errors (array of objects with type, loc, msg, input)
 * - Single error objects
 * - Unknown error formats
 *
 * @param {any} errorDetail - The error detail from error.response.data.detail
 * @returns {string} - A user-friendly error message
 */
export const formatApiError = (errorDetail) => {
  // If it's already a string, return as-is
  if (typeof errorDetail === 'string') {
    return errorDetail;
  }

  // Handle validation errors (array of error objects)
  if (Array.isArray(errorDetail)) {
    return errorDetail
      .map(err => {
        // Handle Pydantic validation errors
        if (err.msg && err.loc) {
          const location = Array.isArray(err.loc) ? err.loc.join('.') : err.loc;
          return `${location}: ${err.msg}`;
        }
        // Handle generic error objects
        return err.msg || err.message || JSON.stringify(err);
      })
      .join(', ');
  }

  // Handle single error object
  if (typeof errorDetail === 'object' && errorDetail !== null) {
    // Handle Pydantic validation error object
    if (errorDetail.msg && errorDetail.loc) {
      const location = Array.isArray(errorDetail.loc) ? errorDetail.loc.join('.') : errorDetail.loc;
      return `${location}: ${errorDetail.msg}`;
    }

    // Handle generic error object
    return errorDetail.msg ||
           errorDetail.message ||
           errorDetail.detail ||
           JSON.stringify(errorDetail);
  }

  // Fallback for unknown error types
  return JSON.stringify(errorDetail);
};

/**
 * Extracts and formats error message from axios error response
 *
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default message if no specific error found
 * @returns {string} - Formatted error message
 */
export const getApiErrorMessage = (error, defaultMessage = 'An unexpected error occurred') => {
  // Check for response error detail
  if (error.response?.data?.detail) {
    return formatApiError(error.response.data.detail);
  }

  // Check for other response error formats
  if (error.response?.data?.message) {
    return formatApiError(error.response.data.message);
  }

  // Check for network or other errors
  if (error.message) {
    return error.message;
  }

  // Fallback to default message
  return defaultMessage;
};

/**
 * Safely renders error content in React components
 * Ensures that the error is always a string before rendering
 *
 * @param {any} error - Error content to render
 * @returns {string} - String safe for React rendering
 */
export const renderError = (error) => {
  if (typeof error === 'string') {
    return error;
  }

  if (error === null || error === undefined) {
    return '';
  }

  return formatApiError(error);
};